import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useForm } from 'react-hook-form';
import emailjs from '@emailjs/browser';
import { Send, Phone, Mail, ArrowRight, CheckCircle2 } from 'lucide-react';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  practiceSize: string;
  currentPlatform: string;
  message: string;
}

export default function ContactForm() {
  const { ref, inView } = useScrollAnimation();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
        {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          phone: data.phone || 'Not provided',
          practice_size: data.practiceSize || 'Not specified',
          current_platform: data.currentPlatform || 'Not specified',
          message: data.message || 'No message',
        },
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY! }
      );
      setIsSubmitted(true);
    } catch (error) {
      console.error('EmailJS error:', error);
      alert('Something went wrong. Please try again or email us directly at hello@doctorite.ai');
    }
  };

  return (
    <section id="contact" className="section-padding bg-gradient-to-b from-gray-50 to-white" ref={ref}>
      <div className="container-max">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
              Ready to Transform{' '}
              <span className="gradient-text">Your Practice?</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Book a personalized demo to see how Doctorite AI can reduce your documentation
              time by 70%, enhance clinical quality, and modernize your practice.
            </p>

            <div className="space-y-4 mb-10">
              <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
                <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-brand-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">Call Us</div>
                  <a href="tel:+16823735380" className="text-sm text-brand-600 hover:text-brand-700">
                    +1 (682) 373-5380
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
                <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-brand-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">Email Us</div>
                  <a href="mailto:hello@doctorite.ai" className="text-sm text-brand-600 hover:text-brand-700">
                    hello@doctorite.ai
                  </a>
                </div>
              </div>
              {/* <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
                <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-brand-600" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">Visit Us</div>
                  <div className="text-sm text-gray-500">18240 Midway Road, Dallas TX 75287</div>
                </div>
              </div> */}
            </div>

            {/* Quick benefits */}
            <div className="bg-brand-50 rounded-xl p-6 border border-brand-100">
              <h4 className="text-sm font-bold text-brand-900 mb-3">What you'll get in your demo:</h4>
              <ul className="space-y-2">
                {[
                  'Live walkthrough of AI session guidance',
                  'Automated documentation demo with real examples',
                  'Custom ROI analysis for your practice size',
                  'Migration plan from your current platform',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-brand-700">
                    <CheckCircle2 className="w-4 h-4 text-accent-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {isSubmitted ? (
              <div className="bg-white rounded-2xl shadow-xl shadow-brand-900/5 border border-gray-100 p-8 text-center">
                <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-accent-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600 mb-6">
                  We've received your request. A member of our team will reach out
                  within 24 hours to schedule your personalized demo.
                </p>
                <Link
                  to="/register"
                  className="btn-primary group"
                >
                  Start Free Trial Now
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="bg-white rounded-2xl shadow-xl shadow-brand-900/5 border border-gray-100 p-6 sm:p-8"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-1">Book Your Free Demo</h3>
                <p className="text-sm text-gray-500 mb-6">No commitment. See the AI in action.</p>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1.5">
                      First Name *
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      {...register('firstName', { required: 'First name is required' })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                      placeholder="Jane"
                    />
                    {errors.firstName && (
                      <p className="text-xs text-red-500 mt-1">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Last Name *
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      {...register('lastName', { required: 'Last name is required' })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                      placeholder="Smith"
                    />
                    {errors.lastName && (
                      <p className="text-xs text-red-500 mt-1">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Work Email *
                  </label>
                  <input
                    id="email"
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' },
                    })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="jane@practice.com"
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div className="mb-4">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    {...register('phone')}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="practiceSize" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Practice Size
                    </label>
                    <select
                      id="practiceSize"
                      {...register('practiceSize')}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors bg-white"
                    >
                      <option value="">Select...</option>
                      <option value="solo">Solo Practice</option>
                      <option value="small">2-10 Providers</option>
                      <option value="medium">11-50 Providers</option>
                      <option value="large">50+ Providers</option>
                      {/* <option value="organization">Organization / BHI Program</option> */}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="currentPlatform" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Current Platform
                    </label>
                    <select
                      id="currentPlatform"
                      {...register('currentPlatform')}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors bg-white"
                    >
                      <option value="">Select...</option>
                      <option value="none">No Platform Yet</option>
                      <option value="simplepractice">SimplePractice</option>
                      <option value="theranest">TheraNest / Ensora</option>
                      <option value="therapynotes">TherapyNotes</option>
                      <option value="valant">Valant</option>
                      <option value="kipu">Kipu Health</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Anything specific you'd like to see?
                  </label>
                  <textarea
                    id="message"
                    rows={3}
                    {...register('message')}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors resize-none"
                    placeholder="Tell us about your practice needs..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full !py-3.5 !text-base group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Book My Free Demo
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center mt-3">
                  By submitting, you agree to our Terms of Use and Privacy Policy.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
